import type { Baseline } from '@shared/models/project.model';
import {
  ProjectChangeType,
  type ProjectChangeMap,
} from '../update-engine.models';
import { addProposal } from './add-proposal.mutator';
import { duplicateProposal } from './duplicate-proposal.mutator';
import { deleteProposal } from './delete-proposal.mutator';
import { updateProposals } from './update-proposals.mutator';
import { clearProposals } from './clear-proposals.mutator';
import { appendProposalAction } from './proposal-append-action.mutator';
import { reorderProposals } from './reorder-proposals.mutator';
import { updateAction } from './update-action.mutator';
import { deleteAction } from './delete-action.mutator';

/**
 * {@link ProjectChangeType}s that mutate version-scoped collections (proposals, proposals,
 * actions) instead of replacing top-level primitives. They are applied via dedicated
 * mutators returning a full {@link Baseline} — never as `Partial<Baseline>` —
 * so concurrent writes in the same batch can't accidentally overwrite untouched fields.
 *
 * Pure primitive replacements ({@link ProjectChangeType.VersionName},
 * {@link ProjectChangeType.VersionProducts}, {@link ProjectChangeType.VersionResults}) stay
 * in the partial-update path (see `version-change.update.ts`).
 */
export type VersionMutateChangeType =
  | ProjectChangeType.ProposalResultsRecord
  | ProjectChangeType.VersionClearProposals
  | ProjectChangeType.ProposalAdd
  | ProjectChangeType.ProposalDuplicate
  | ProjectChangeType.ProposalDelete
  | ProjectChangeType.ProposalUpdate
  | ProjectChangeType.ProposalAppendAction
  | ProjectChangeType.ProposalReorder
  | ProjectChangeType.ActionRename
  | ProjectChangeType.ActionDelete;

export type VersionMutateChange = ProjectChangeMap[VersionMutateChangeType];

/** Runtime list of {@link VersionMutateChangeType} values, used by the categorizer. */
export const versionMutateChangeTypes: readonly VersionMutateChangeType[] = [
  ProjectChangeType.ProposalResultsRecord,
  ProjectChangeType.VersionClearProposals,
  ProjectChangeType.ProposalAdd,
  ProjectChangeType.ProposalDuplicate,
  ProjectChangeType.ProposalDelete,
  ProjectChangeType.ProposalUpdate,
  ProjectChangeType.ProposalAppendAction,
  ProjectChangeType.ProposalReorder,
  ProjectChangeType.ActionRename,
  ProjectChangeType.ActionDelete,
];

/**
 * Apply a single version-scoped structural change to a {@link Baseline}.
 *
 * Each branch delegates to a dedicated mutator that returns a full updated version (or the
 * same reference when there is nothing to do). Composes cleanly when chained per version
 * by {@link applyVersionMutateChanges} so subsequent mutators see prior results.
 *
 * @param version - Current version state
 * @param change - Version-scoped mutate change
 * @returns Updated version (same reference when nothing changed)
 */
export const applyVersionMutateChange = (
  version: Baseline,
  change: VersionMutateChange,
): Baseline => {
  switch (change.type) {
    case ProjectChangeType.ProposalResultsRecord:
      return updateProposals(version, {
        id: change.proposalId,
        resultsRecord: change.resultsRecord,
      });
    case ProjectChangeType.ProposalDelete:
      return deleteProposal(version, change.proposalId);
    case ProjectChangeType.ProposalUpdate:
      return updateProposals(version, ...change.updates);
    case ProjectChangeType.VersionClearProposals:
      return clearProposals(version);
    case ProjectChangeType.ProposalAdd:
      return addProposal(version, change.defaults, change.proposalId);
    case ProjectChangeType.ProposalDuplicate:
      return duplicateProposal(version, change.proposalId);
    case ProjectChangeType.ProposalAppendAction:
      return appendProposalAction(version, change.proposalId, change.changes);
    case ProjectChangeType.ProposalReorder:
      return reorderProposals(version, change.orderedIds);
    case ProjectChangeType.ActionRename:
      return updateAction(version, change.actionId, { name: change.name });
    case ProjectChangeType.ActionDelete:
      return deleteAction(version, change.actionId);
  }
};
